<script setup lang="ts">
import type { Drill, DrillSet } from '@functions/types/drills';
import { format } from 'date-fns';
import { collection, orderBy, query } from 'firebase/firestore';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useCollection } from 'vuefire';
import { useDebugUser } from '@/composables/useDebugUser';
import { db } from '@/firebase';
import Container from '@/layouts/Container.vue';

definePage({
  name: 'drills',
});

const router = useRouter();
const user = useDebugUser();

// Filter and sort options
const searchTerm = ref('');
const selectedDifficulties = ref<string[]>([]);
const difficultyOptions = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];
const sortOption = ref('createdAt');
const sortOptions = [
  { value: 'createdAt', label: 'Date (Newest First)' },
  { value: 'createdAt_asc', label: 'Date (Oldest First)' },
  { value: 'drill_count', label: 'Number of Drills' },
];
const useMonitorFilter = ref<string | null>(null);
const monitorOptions = [
  { value: 'yes', label: 'Uses Launch Monitor' },
  { value: 'no', label: 'No Launch Monitor Required' },
];

// Fetch all drill sets
const drillsQuery = computed(() => {
  if (!user.value?.uid) return null;

  const collectionRef = collection(db, `drills/${user.value.uid}/sets`);
  if (sortOption.value === 'createdAt') {
    return query(collectionRef, orderBy('createdAt', 'desc'));
  }
  else if (sortOption.value === 'createdAt_asc') {
    return query(collectionRef, orderBy('createdAt', 'asc'));
  }
  else {
    return query(collectionRef, orderBy('createdAt', 'desc')); // Default
  }
});

const { data: drillSets, pending } = useCollection<DrillSet>(drillsQuery);

// Apply filters to the drill sets
const filteredDrillSets = computed(() => {
  if (!drillSets.value) return [];

  let filtered = [...drillSets.value];

  // Apply custom sorting for drill count
  if (sortOption.value === 'drill_count') {
    filtered.sort((a, b) => b.drills.length - a.drills.length);
  }

  // Apply search filter
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    filtered = filtered.filter((drillSet) => {
      // Search in drill names, purposes, etc.
      return drillSet.drills.some(drill =>
        drill.name.toLowerCase().includes(term)
        || drill.purpose.toLowerCase().includes(term));
    });
  }

  // Filter drill sets based on whether they contain drills matching the selected difficulties
  if (selectedDifficulties.value.length) {
    filtered = filtered.filter((drillSet) => {
      return drillSet.drills.some(drill =>
        selectedDifficulties.value.includes(drill.difficulty));
    });
  }

  // Filter by launch monitor requirement
  if (useMonitorFilter.value) {
    filtered = filtered.filter((drillSet) => {
      if (useMonitorFilter.value === 'yes') {
        return drillSet.drills.some(drill => drill.usesLaunchMonitor);
      }
      else {
        return drillSet.drills.some(drill => !drill.usesLaunchMonitor);
      }
    });
  }

  return filtered;
});

// Get a summary of drill types in a drill set for display
function getDrillSummary(drillSet: DrillSet) {
  // Count drills by difficulty
  const summary = {
    beginner: 0,
    intermediate: 0,
    advanced: 0,
    usesMonitor: 0,
  };

  drillSet.drills.forEach((drill: Drill) => {
    if (drill.difficulty) {
      summary[drill.difficulty as keyof typeof summary]++;
    }
    if (drill.usesLaunchMonitor) {
      summary.usesMonitor++;
    }
  });

  return summary;
}

function viewDrillSet(drillSetId: string) {
  router.push(`/drills/${drillSetId}`);
}

// Clear all filters
function clearFilters() {
  searchTerm.value = '';
  selectedDifficulties.value = [];
  useMonitorFilter.value = null;
  sortOption.value = 'createdAt';
}
</script>

<template>
  <Container>
    <template #title>
      Practice Plans
    </template>

    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
        Your Practice Plans
      </h2>
      <p class="mt-2 text-gray-600 dark:text-gray-400">
        Custom practice plans based on your performance data.
      </p>
    </div>

    <!-- Filter and Search Bar -->
    <div class="bg-white p-4 rounded-lg shadow-sm mb-6">
      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <!-- Search -->
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <span class="p-input-icon-left w-full">
            <InputText v-model="searchTerm" placeholder="Search drills..." class="w-full" />
          </span>
        </div>

        <!-- Difficulty Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
          <MultiSelect
            v-model="selectedDifficulties"
            :options="difficultyOptions"
            option-label="label"
            option-value="value"
            placeholder="All Difficulties"
            class="w-full"
          />
        </div>

        <!-- Launch Monitor Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Equipment</label>
          <Dropdown
            v-model="useMonitorFilter"
            :options="monitorOptions"
            option-label="label"
            option-value="value"
            placeholder="All Drills"
            class="w-full"
          />
        </div>

        <!-- Sort Option -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
          <Dropdown
            v-model="sortOption"
            :options="sortOptions"
            option-label="label"
            option-value="value"
            class="w-full"
          />
        </div>
      </div>

      <!-- Filter Summary & Clear -->
      <div v-if="searchTerm || selectedDifficulties.length || useMonitorFilter" class="flex justify-between items-center mt-4 pt-3 border-t border-gray-200">
        <div class="text-sm text-gray-600">
          <span v-if="filteredDrillSets.length === 1">1 drill set found</span>
          <span v-else>{{ filteredDrillSets.length }} drill sets found</span>
        </div>
        <Button
          icon="pi pi-filter-slash"
          label="Clear Filters"
          class="p-button-sm p-button-text"
          @click="clearFilters"
        />
      </div>
    </div>

    <div v-if="pending" class="flex min-h-[60vh] items-center justify-center flex-col gap-2">
      <div class="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      <p class="text-gray-900 dark:text-gray-200">
        Loading drills...
      </p>
    </div>

    <div v-else-if="drillSets?.length === 0" class="flex min-h-[40vh] items-center justify-center flex-col">
      <p class="text-gray-600 dark:text-gray-400 mb-4">
        You don't have any practice plans yet.
      </p>
      <router-link
        to="/reports"
        class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
      >
        View your reports to generate drills
      </router-link>
    </div>

    <div v-else-if="filteredDrillSets.length === 0" class="flex min-h-[40vh] items-center justify-center flex-col">
      <i class="pi pi-filter-slash text-5xl text-gray-300 mb-4" />
      <p class="text-gray-600 dark:text-gray-400 mb-4">
        No drills match your current filters.
      </p>
      <Button
        icon="pi pi-filter-slash"
        label="Clear Filters"
        class="p-button-sm"
        @click="clearFilters"
      />
    </div>

    <div v-else class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="drillSet in filteredDrillSets"
        :key="drillSet.id"
        class="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
        @click="viewDrillSet(drillSet.id)"
      >
        <div class="flex justify-between items-start mb-3">
          <h3 class="text-lg font-medium text-gray-900">
            Practice Set
          </h3>
          <span class="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
            {{ drillSet.drills.length }} drills
          </span>
        </div>

        <p class="text-gray-500 text-sm mb-3">
          Created {{ format(new Date(drillSet.createdAt.toDate()), 'PPP') }}
        </p>

        <div class="space-y-2 mb-4">
          <div v-for="(drill, index) in drillSet.drills.slice(0, 2)" :key="index" class="text-sm text-gray-700 truncate">
            • {{ drill.name }}
          </div>
          <div v-if="drillSet.drills.length > 2" class="text-sm text-gray-500 italic">
            + {{ drillSet.drills.length - 2 }} more drills
          </div>
        </div>

        <div class="flex flex-wrap gap-2 mb-3">
          <div v-if="getDrillSummary(drillSet).beginner > 0" class="text-xs py-1 px-2 rounded-full bg-green-100 text-green-800">
            {{ getDrillSummary(drillSet).beginner }} Beginner Drills
          </div>
          <div v-if="getDrillSummary(drillSet).intermediate > 0" class="text-xs py-1 px-2 rounded-full bg-yellow-100 text-yellow-800">
            {{ getDrillSummary(drillSet).intermediate }} Intermediate Drills
          </div>
          <div v-if="getDrillSummary(drillSet).advanced > 0" class="text-xs py-1 px-2 rounded-full bg-red-100 text-red-800">
            {{ getDrillSummary(drillSet).advanced }} Advanced Drills
          </div>
          <div v-if="getDrillSummary(drillSet).usesMonitor > 0" class="text-xs py-1 px-2 rounded-full bg-indigo-100 text-indigo-800">
            {{ getDrillSummary(drillSet).usesMonitor }} Launch Monitor Drills
          </div>
        </div>

        <div class="mt-1 flex justify-end">
          <button class="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
            View details
            <i class="pi pi-arrow-right" />
          </button>
        </div>
      </div>
    </div>
  </Container>
</template>
